import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

// ✅ Section Component (collapsible)
const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 border">
      <div
        className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
        onClick={() => setOpen(!open)}

      >
        <span>{title}</span>
        <span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-3 p-3">{children}</div>}
    </div>
  );
};

export default function EmployeeView() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [employee, setEmployee] = useState(null);
  const [accessInfo, setAccessInfo] = useState({});
  const [activeTab, setActiveTab] = useState("comm");
  const [bankDetails, setBankDetails] = useState(null);
  const [user, setUser] = useState(null);
  const [supervisor, setSupervisor] = useState(null);
  const [systemNotes, setSystemNotes] = useState([]);
  const [isActive, setIsActive] = useState(true); // default active
  const [leaves, setLeaves] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const navbarColor = sessionStorage.getItem("navbarColor") || "#080235";
  const [activeSubTab, setActiveSubTab] = useState("systemNotes");
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [employeeIds, setEmployeeIds] = useState([]);
  const [employees, setEmployees] = useState([]); // <-- add this








  const filteredLeaves = leaves.filter((leave) => {
    const statusMatch = filter === "ALL" || leave.status === filter;

    const leaveStart = new Date(leave.startDate);
    const leaveEnd = new Date(leave.endDate);

    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;

    const dateMatch =
      (!from || leaveEnd >= from) &&
      (!to || leaveStart <= to);

    return statusMatch && dateMatch;
  });





  // useEffect(() => {
  //   if (employee?.user?.id) {
  //     fetch(`${API_BASE_URL}/api/leaves/user/${employee.user.id}`)
  //       .then((res) => res.json())
  //       .then((data) => setLeaves(data))
  //       .catch((err) => console.error("Failed to fetch leaves", err));
  //   }
  // }, [employee]);


  useEffect(() => {
    const token = sessionStorage.getItem("token");
    fetch(`${API_BASE_URL}/api/employees`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        // normalize possible object -> array
        const empArray = Array.isArray(data) ? data : Object.values(data || {});

        setEmployees(empArray);

        // Store sorted numeric IDs (or map to actual unique field)
        const ids = empArray
          .map((emp) => Number(emp.id))
          .filter((id) => !Number.isNaN(id))
          .sort((a, b) => a - b);
        setEmployeeIds(ids);
        setTotalEmployees(empArray.length);
      })
      .catch((err) => console.error("Failed to fetch employee list:", err));
  }, []);


  const empMap = React.useMemo(() => {
    if (!Array.isArray(employees)) return {};
    return employees.reduce((acc, e) => {
      const id = String(e.id ?? e.employeeId ?? e.userId ?? "");
      const name =
        e.name ??
        (e.firstName || e.lastName ? `${e.firstName ?? ""} ${e.lastName ?? ""}`.trim() : null) ??
        e.fullName ??
        null;
      if (id) acc[id] = name || id;
      return acc;
    }, {});
  }, [employees]);


  // 🔐 Authentication check
  useEffect(() => {
  const token = sessionStorage.getItem("token");
  const roles = JSON.parse(sessionStorage.getItem("roles") || "[]");

  if (!token) {
    toast.error("You must be logged in to access this page.");
    navigate("/");
    return;
  }

  // ✅ check admin role
  if (!roles.includes("ROLE_ADMIN")) {
    toast.error("You are not authorized to access this page.");
    navigate("/");
    return;
  }
}, [navigate]);

  // Utility to mask sensitive info
  const maskValue = (value, visible = 2) => {
    if (!value) return "—";
    const str = value.toString();
    if (str.length <= visible) return str;
    return "*".repeat(str.length - visible) + str.slice(-visible);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Human-readable labels for employee fields
  const fieldLabels = {
    // Basic Info
    employeeId:"EmployeeId",
    firstName: "First Name",
    lastName: "Last Name",
    email: "Work Email",
    pemail: "Personal Email",
    phone: "Phone Number",
    dob: "Date of Birth",
    gender: "Gender",
    mstatus: "Marital Status",

    // Job Info
    designation: "Job role",
    department: "Department",
    doj: "Date of Joining",
    doa: "Date of Appointment",
    dol: "Date of Leaving",
    superviser: "Supervisor",
    status: "Employment Status",
    role: "Roles",
    selectedRole: "Selected Role",

    // Address (Permanent)
    street1: "Street Address 1",
    street2: "Street Address 2",
    city: "City",
    state: "State",
    postalCode: "Postal Code",
    country: "Country",

    // Address (Current)
    sameAsPermanent: "Same as Permanent Address",
    currStreet1: "Current Street Address 1",
    currStreet2: "Current Street Address 2",
    currCity: "Current City",
    currState: "Current State",
    currPostalCode: "Current Postal Code",
    currCountry: "Current Country",

    // Education
    degree: "Degree",
    institution: "University",
    fieldOfStudy: "Stream",
    eduStartDate: "Education Start Date",
    eduEndDate: "Education End Date",
    grade: "Grade",

    // Emergency / ID
    contact: "Emergency Contact",
    alno: "Alternative No",

    // Bank Info
    bankName: "Bank Name",
    accountNumber: "Account Number",
    ifscCode: "IFSC Code",
    branch: "Branch",

    // Security
    password: "Password",
    confirmPassword: "Confirm Password",

    // System / Audit
    createdDate: "Created Date",
    modifiedDate: "Modified Date",
    modifiedBy: "Modified By",
  };


  // ✅ Load bootstrap CSS dynamically
  useEffect(() => {
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(bootstrapLink);

    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(iconsLink);

    return () => {
      bootstrapLink.remove();
      iconsLink.remove();
    };
  }, []);

  useEffect(() => {
    const fetchSystemNotes = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/audit/employee/${id}`, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
        });
        if (res.ok) {
          const data = await res.json();
          setSystemNotes(data);
        } else {
          setSystemNotes([]);
        }
      } catch (err) {
        console.error("Failed to fetch system notes", err);
      }
    };

    fetchSystemNotes();
  }, [id]);


  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (employee?.superviser) {
      fetch(`${API_BASE_URL}/api/employees/${employee.superviser}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.ok ? res.json() : null)
        .then((data) => setSupervisor(data))
        .catch(() => console.warn("⚠️ No supervisor found"));
    }
  }, [employee]);



  useEffect(() => {
    const token = sessionStorage.getItem("token");

    // Fetch employee
    fetch(`${API_BASE_URL}/api/employees/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setEmployee(data);

        // Fetch Access Info
        if (data.email) {
          return fetch(`${API_BASE_URL}/api/users/employee/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }
      })
      .then((res) => (res ? res.json() : null))
      .then((access) => setAccessInfo(access))
      .catch((err) => console.error("Error loading employee", err));

    // Fetch Bank Details
    fetch(`${API_BASE_URL}/api/bank-details/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setBankDetails(data))
      .catch(() => console.warn("⚠️ No bank details found"));
  }, [id]);


  if (!employee) return <div className="p-4">Loading...</div>;

  return (
    <>
      <div className="fixed-top">
        <AdminNavbar />
      </div>
      <div className="container-fluid my-4" style={{ paddingTop: "110px" }}>
        {/* Header */}
        {/* Employee Header */}
        <div className="border-bottom pb-2 mb-3">
          {/* Top Row: Title + Navigation */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center">
              <i className="bi bi-people fs-3 me-2 text-primary"></i>
              <h5 className="m-0 me-3">Employee</h5>
              {/* <i className="bi bi-search text-muted" style={{ cursor: "pointer" }}></i> */}
            </div>

            {/* Navigation & List/Search (Right) */}
            <div className="d-flex align-items-center text-muted">
              {/* Backward Arrow */}
              <i
                className="bi bi-arrow-left-circle me-3"
                style={{ cursor: "pointer", fontSize: "1.3rem", color: "#0d6efd" }}
                title="Previous Employee"
                onClick={() => {
                  if (employeeIds.length > 0) {
                    const currentIndex = employeeIds.indexOf(parseInt(id));
                    if (currentIndex > 0) {
                      const prevId = employeeIds[currentIndex - 1];
                      navigate(`/viewemployee/${prevId}`);
                    } else {
                      toast.info("This is the first employee in the list.");
                    }
                  }
                }}
              ></i>

              {/* Forward Arrow */}
              <i
                className="bi bi-arrow-right-circle me-3"
                style={{ cursor: "pointer", fontSize: "1.3rem", color: "#0d6efd" }}
                title="Next Employee"
                onClick={() => {
                  if (employeeIds.length > 0) {
                    const currentIndex = employeeIds.indexOf(parseInt(id));
                    if (currentIndex < employeeIds.length - 1) {
                      const nextId = employeeIds[currentIndex + 1];
                      navigate(`/viewemployee/${nextId}`);
                    } else {
                      toast.info("This is the last employee in the list.");
                    }
                  }
                }}
              ></i>

              {/* List Button */}
              <span
                className="me-2"
                style={{ cursor: "pointer", color: "blue" }}
                onClick={() => navigate("/allemployee")}
              >
                List
              </span>
            </div>



          </div>

          {/* Second Row: Name + Status */}
          <div className="d-flex align-items-center mb-2">
            {/* 👤 Employee Photo */}
            {employee.photoPath && (
              <img
                src={`${API_BASE_URL}/uploads/${employee.photoPath}`}
                alt="Employee"
                className="rounded-circle me-3"
                style={{ width: "40px", height: "40px", objectFit: "cover" }}
              />
            )}

            {/* Name */}
            <h4 className="m-0 me-3">
              {employee.firstName} {employee.lastName}
            </h4>

            {/* Status */}
            <span
              className={`badge ${employee.user?.status === "Active" ? "bg-success" : "bg-secondary"
                }`}
            >
              {employee.user?.status?.toUpperCase() || "UNKNOWN"}
            </span>
          </div>


          {/* Third Row: Action Buttons */}
          <div className="d-flex align-items-center mb-2">
            <button
              className="btn btn-primary btn-sm me-2"
              onClick={() => navigate(`/editemployee/${id}`)}
            >
              Edit
            </button>
            <button
              className="btn btn-light btn-sm me-2"
              onClick={() => navigate("/allemployee")}
            >
              Back
            </button>

            {/* Actions Dropdown
    <div className="dropdown">
      <button
        className="btn btn-light btn-sm dropdown-toggle"
        type="button"
        id="actionsDropdown"
        data-bs-toggle="dropdown"
        aria-expanded="false"
      >
        Actions
      </button>
      <ul className="dropdown-menu" aria-labelledby="actionsDropdown">
        <li>
          <button className="dropdown-item">Download PDF</button>
        </li>
        <li>
          <button className="dropdown-item">Delete</button>
        </li>
        <li>
          <button className="dropdown-item">More...</button>
        </li>
      </ul>
    </div> */}
          </div>
        </div>



        {/* Primary Info */}
        <Section title="Primary Information" >
          <div className="col-md-3"><strong>Name</strong> <br />{employee.firstName} {employee.lastName}</div>
          <div className="col-md-3"><strong>Job Role</strong><br /> {employee.designation}</div>
          <div className="col-md-3"><strong>Department</strong> <br />{employee.department}</div>
          <div className="col-md-3"><strong>Supervisor</strong><br />{supervisor ? `${supervisor.firstName} ${supervisor.lastName}` : ""}</div>
          <div className="col-md-3"><strong>Employee ID</strong> <br />{employee.employeeId}</div>

        </Section>



        {/* Email / Phone */}
        <Section title="Email | Phone |">
          <div className="col-md-3"><strong>Work Email</strong><br /> {employee.email}</div>
          <div className="col-md-3"><strong>Country</strong><br /> {employee.country}</div>
          <div className="col-md-3"><strong>Office Phone</strong><br /> {employee.phone}</div>
        </Section>




        {/* Classification */}
        {/* <Section title="Classification">          
        </Section> */}

        {/* Tabs */}
        <ul
          className="nav nav-tabs mb-2 fw-semibold"
          style={{ backgroundColor: navbarColor }}
        >
          {[
            ["comm", "Communication"],
            ["hr", "Human Resources"],
            ["access", "Access"],
            // ["time", "Time Tracking"],
            // ["commission", "Commission"],
            // ["marketing", "Marketing"],
            // ["leaveinfo", "Leave Info"],
            ["bank", "Bank Payment Details"],
            ["system", "System Information"],
          ].map(([id, label]) => (
            <li className="nav-item" key={id}>
              <button
                type="button"
                className={`nav-link ${activeTab === id ? "active" : ""}`}
                onClick={() => setActiveTab(id)}
                style={{
                  color: activeTab === id ? navbarColor : "#fff",
                  backgroundColor: activeTab === id ? "#fff" : "transparent",
                  fontWeight: activeTab === id ? "600" : "normal",
                }}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>




        <div className="border p-3">
          {activeTab === "comm" && <div>📩 Communication Tab Data (from backend if available)</div>}
          {activeTab === "hr" && (
            <>
              {/* <div>👥 HR Data here</div> */}

              <Section title="Primary Information" >

                <div className="col-md-3"><strong>Personal Email</strong><br /> {employee.pemail}</div>
                <div className="col-md-3"><strong>Contact No</strong><br /> {employee.contact}</div>
                <div className="col-md-3"><strong>Alternative No</strong><br /> {employee.alno}</div>
                <div className="col-md-3"><strong>DOB</strong> <br />{employee.dob ? new Date(employee.dob).toLocaleDateString("en-GB") : ""}</div>
                <div className="col-md-3"><strong>Marital Status</strong><br /> {employee.mstatus}</div>
                <div className="col-md-3"><strong>Anniversary</strong><br /> {employee.doa ? new Date(employee.doa).toLocaleDateString("en-GB") : ""}</div>
                <div className="col-md-3"><strong>Gender</strong> <br />{employee.gender}</div>
                <div className="col-md-3"><strong>Joining Date</strong> <br />{employee.doj ? new Date(employee.doj).toLocaleDateString("en-GB") : ""}</div>
                <div className="col-md-3"><strong>Leaving Date</strong><br /> {employee.dol ? new Date(employee.dol).toLocaleDateString("en-GB") : "" || ""}</div>
                {employee.photoPath && (
                  <div className="col-md-3">
                    <strong>Photo</strong><br />
                    <img
                      src={`${API_BASE_URL}/uploads/${employee.photoPath}`}
                      alt="Employee"
                      className="img-thumbnail"
                      style={{ width: "120px", height: "120px" }}
                    />
                  </div>
                )}

              </Section>

              {/* Education */}
              <Section title="Education Information">
                <div className="col-md-3">
                  <strong>Degree</strong> <br />
                  {employee.degree}
                </div>

                <div className="col-md-3">
                  <strong>University</strong> <br />
                  {employee.institution}
                </div>

                <div className="col-md-3">
                  <strong>Stream</strong> <br />
                  {employee.fieldOfStudy}
                </div>

                <div className="col-md-3">
                  <strong>Start Date</strong> <br />
                  {employee.eduStartDate
                    ? new Date(employee.eduStartDate).toLocaleDateString("en-GB")
                    : ""}
                </div>

                <div className="col-md-3">
                  <strong>End Date</strong> <br />
                  {employee.eduEndDate
                    ? new Date(employee.eduEndDate).toLocaleDateString("en-GB")
                    : ""}
                </div>

                <div className="col-md-3">
                  <strong>Grade</strong> <br />
                  {employee.grade}
                </div>
              </Section>

              {/* Address */}
              <Section title="Address Information">
                <div className="row">
                  {/* Permanent Address */}
                  <div className="col-md-4">
                    <h6 className="fw-bold">Permanent Address</h6>
                    <p>
                      {employee.street1}, {employee.street2}, {employee.city}, {employee.state},{" "}
                      {employee.postalCode}, {employee.country}
                    </p>
                  </div>

                  {/* Current Address */}
                  <div className="col-md-4">
                    <h6 className="fw-bold">Current Address</h6>
                    <p>
                      {employee.currStreet1}, {employee.currStreet2}, {employee.currCity},{" "}
                      {employee.currState}, {employee.currPostalCode}, {employee.currCountry}
                    </p>
                  </div>
                </div>
              </Section>
            </>
          )}

          {activeTab === "time" && <div>⏱ Time Tracking Data</div>}
          {activeTab === "commission" && <div>💰 Commission Data</div>}
          {activeTab === "marketing" && <div>📊 Marketing Data</div>}
          {/* {activeTab === "leaveinfo" && (
            <div>
              <h5>📊 Leave Data</h5>

             
              <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                <select
                  className="form-select w-auto"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="ALL">All</option>
                  <option value="APPROVED">Approved</option>
                  <option value="PENDING">Pending</option>
                  <option value="REJECTED">Rejected</option>
                </select>

                <input
                  type="date"
                  className="form-control w-auto"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
                <span>to</span>
                <input
                  type="date"
                  className="form-control w-auto"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              {filteredLeaves.length === 0 ? (
                <p className="text-muted">No leave requests found.</p>
              ) : (
                <table className="table table-sm text-center align-middle">
                  <thead className="table ">
                    <tr>
                      <th className="fw-normal">Type</th>
                      <th className="fw-normal">From</th>
                      <th className="fw-normal">To</th>
                      <th className="fw-normal">Reason</th>
                      <th className="fw-normal">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeaves.map((leave) => (
                      <tr key={leave.id}>
                        <td className="fw-light">{leave.leaveType}</td>
                        <td className="fw-light">{leave.startDate ? new Date(leave.startDate).toLocaleDateString("en-GB") : ""}</td>
                        <td className="fw-light">{leave.endDate ? new Date(leave.endDate).toLocaleDateString("en-GB") : ""}</td>
                        <td className="fw-light">{leave.reason}</td>
                        <td className="fw-light">
                          <span
                          // className={`badge ${
                          //   leave.status === "APPROVED"
                          //     ? "bg-success"
                          //     : leave.status === "REJECTED"
                          //     ? "bg-danger"
                          //     : "bg-warning"
                          // }`}
                          >
                            {leave.status}
                          </span>
                          {leave.status === "REJECTED" && (
                            <div className="small text-muted">{leave.rejectionReason}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )} */}

          {activeTab === "bank" && (
            <div>
              <h5> Bank Payment Details</h5>
              {bankDetails ? (
                <div className="row">
                  <div className="col-md-4"><strong>Bank Name</strong><br /> {bankDetails.bankName || ""}</div>
                  <div className="col-md-4"><strong>Branch</strong> <br />{bankDetails.branch || ""}</div>
                  <div className="col-md-4"><strong>Account No</strong><br /> {maskValue(bankDetails.accountNumber)}</div>
                  <div className="col-md-4"><strong>IFSC Code</strong><br /> {maskValue(bankDetails.ifscCode, 3)}</div>
                  {/* <div className="col-md-4"><strong>Payment Type:</strong> {bankDetails.paymentType || ""}</div> */}
                </div>
              ) : (
                <p className="text-muted">No bank details found.</p>
              )}
            </div>
          )}


          {activeTab === "access" && (
            <div>
              {/* <h5>🔑 Access Information</h5> */}
              {accessInfo ? (
                <>
                  <p><strong>Username</strong> <br />{accessInfo.name || ""}</p>
                  <p><strong>Email</strong><br /> {accessInfo.email || ""}</p>
                  <p><strong>Status</strong><br /> {accessInfo.status || ""}</p>
                  {/* <p><strong>Password</strong><br/> {maskValue(accessInfo.password || "—")}</p> */}
                  <p><strong>Roles</strong><br /> {accessInfo.roles ? accessInfo.roles.join(", ") : ""}</p>
                </>
              ) : (
                <p className="text-muted">No access info available.</p>
              )}
            </div>
          )}

          {activeTab === "system" && (
            <div className="p-3 border rounded bg-white">
              {/* ✅ Date Created + Inactive Checkbox */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="text-muted small">
                  <strong>Date Created:</strong>{" "}
                  {employee.createdDate ? formatDateTime(employee.createdDate) : ""}
                </div>

                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="inactiveCheckbox"
                    checked={!isActive} // checked = inactive
                    disabled
                  />
                  <label className="form-check-label ms-2 fw-bold" htmlFor="inactiveCheckbox">
                    INACTIVE
                  </label>
                </div>
              </div>

              {/* ✅ Sub-tabs */}
              <ul className="nav nav-tabs mb-3">
                {[
                  ["systemNotes", "System Notes"],
                  // ["workflows", "Active Workflows"],
                  // ["history", "Workflow History"],
                ].map(([id, label]) => (
                  <li className="nav-item" key={id}>
                    <button
                      className={`nav-link ${activeSubTab === id ? "active" : ""}`}
                      onClick={() => setActiveSubTab(id)}
                    >
                      {label}
                    </button>
                  </li>
                ))}
              </ul>

              {/* ✅ Content per Sub-tab */}
              {activeSubTab === "systemNotes" && (
                <>

                  {/* Filter Controls */}
                  {/* <div className="d-flex align-items-center mb-3">
                    <label className="me-2 fw-semibold">View</label>
                    <select className="form-select me-3 w-auto">
                      <option>Default</option>
                    </select>

                    <label className="me-2 fw-semibold">Field</label>
                    <select className="form-select me-3 w-auto">
                      <option>- All -</option>
                    </select>

                    <button className="btn btn-outline-secondary btn-sm">
                      Customize View
                    </button>
                  </div> */}

                  {/* System Notes Table */}
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th className="text-start" style={{ backgroundColor: "#DFE4EB" }}>Date</th>
                        <th className="text-start" style={{ backgroundColor: "#DFE4EB" }}>Set By</th>
                        <th className="text-start" style={{ backgroundColor: "#DFE4EB" }}>Field</th>
                        <th className="text-start" style={{ backgroundColor: "#DFE4EB" }}>Old Value</th>
                        <th className="text-start" style={{ backgroundColor: "#DFE4EB" }}>New Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {systemNotes.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-muted text-center">
                            No records to show.
                          </td>
                        </tr>
                      ) : (
                        systemNotes.map((note, i) => (
                          <tr key={i}>
                            <td className="text-start align-middle">{formatDateTime(note.modifiedDate)}</td>
                            <td className="text-start align-middle">{note.modifiedBy}</td>
                            <td className="text-start align-middle">
                              {fieldLabels[note.fieldName] || note.fieldName}
                            </td>
                            <td className="text-start align-middle">
                              {(() => {
                                // fields that contain employee IDs (adjust names if your API uses different keys)
                                const idFields = ["superviser", "supervisor", "reporterId", "assignee", "assignedTo"];
                                if (idFields.includes(note.fieldName?.toLowerCase())) {
                                  return empMap[String(note.oldValue)] ?? note.oldValue ?? "—";
                                }
                                return note.oldValue ?? "—";
                              })()}
                            </td>

                            <td className="text-start align-middle">
                              {(() => {
                                const idFields = ["superviser", "supervisor", "reporterId", "assignee", "assignedTo"];
                                if (idFields.includes(note.fieldName?.toLowerCase())) {
                                  return empMap[String(note.newValue)] ?? note.newValue ?? "—";
                                }
                                return note.newValue ?? "—";
                              })()}
                            </td>

                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>


                </>
              )}

              {activeSubTab === "workflows" && (
                <div className="text-muted"> Active Workflows data will appear here.</div>
              )}

              {activeSubTab === "history" && (
                <div className="text-muted"> Workflow History will appear here.</div>
              )}
            </div>
          )}

        </div>
      </div>
    </>
  );
}
